import { useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  FileText, Image as ImageIcon, Upload, Trash2, Eye, Download,
} from "lucide-react";
import { toast } from "sonner";
import { useFileDrop } from "@/lib/useFileDrop";

const ALLOWED = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.dwg,.dxf,.tiff";

function planIconFor(plan) {
  const ct = (plan.content_type || "").toLowerCase();
  const fn = (plan.filename || "").toLowerCase();
  if (ct.startsWith("image/") || /\.(png|jpe?g|webp|heic|heif|tiff)$/.test(fn)) {
    return ImageIcon;
  }
  return FileText;
}

function viewUrl(path) {
  const token = localStorage.getItem("fl_token");
  return `${API}/files/view?path=${encodeURIComponent(path)}${token ? `&auth=${token}` : ""}`;
}

function bytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

/**
 * Render a list of job plans plus (when editable) an upload button + delete.
 * - editable: office/admin only
 * - compact: smaller rendering for schedule/tomorrow card
 */
export function JobPlans({ jobId, plans = [], editable = false, compact = false, onChange }) {
  const [busy, setBusy] = useState(false);

  const { dragging, dropProps } = useFileDrop((f) => handleFiles(f), { disabled: busy });

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/jobs/${jobId}/plans`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data);
      }
      toast.success(`Uploaded ${added.length} plan${added.length === 1 ? "" : "s"}`);
      onChange?.([...(plans || []), ...added]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Plan upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (plan) => {
    if (!window.confirm(`Remove "${plan.filename}" from this job?`)) return;
    try {
      await api.delete(`/jobs/${jobId}/plans/${plan.id}`);
      onChange?.((plans || []).filter((p) => p.id !== plan.id));
      toast.success("Plan removed");
    } catch (err) {
      toast.error("Couldn't remove plan");
    }
  };

  return (
    <div className="space-y-2" data-testid={`job-plans-${jobId}`}>
      {plans.length === 0 ? (
        <div className={`text-xs ${compact ? "text-zinc-400 italic" : "text-zinc-500"}`} data-testid="plans-empty">
          {editable ? "No plans uploaded yet." : "No plans for this job."}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {plans.map((p) => {
            const Icon = planIconFor(p);
            return (
              <li key={p.id}
                className={`flex items-center gap-2 border border-zinc-300 bg-white ${compact ? "px-2 py-1.5" : "px-3 py-2"}`}
                data-testid={`plan-${p.id}`}>
                <Icon className="w-4 h-4 text-[#FF5F15] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold truncate">{p.filename}</div>
                  <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    {bytes(p.size)} · {p.uploaded_by_name?.split(" ")[0] || "office"}
                  </div>
                </div>
                <a
                  href={viewUrl(p.storage_path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold uppercase tracking-wider text-zinc-700 hover:text-[#FF5F15] inline-flex items-center gap-1 px-2 py-1"
                  data-testid={`plan-view-${p.id}`}
                  title="View / download">
                  <Eye className="w-3.5 h-3.5" />
                  {!compact && <span>View</span>}
                </a>
                {editable && (
                  <button
                    type="button"
                    onClick={() => onDelete(p)}
                    className="text-zinc-400 hover:text-red-600 p-1"
                    data-testid={`plan-delete-${p.id}`}
                    title="Remove plan">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editable && (
        <label className={`inline-flex items-center gap-2 px-3 py-2 border-2 border-dashed text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors ${busy ? "opacity-50 pointer-events-none border-zinc-400" : dragging ? "border-[#FF5F15] bg-orange-50 text-[#FF5F15]" : "border-zinc-400 hover:border-[#FF5F15] hover:text-[#FF5F15]"}`}
          {...dropProps}
          data-testid={`plan-upload-${jobId}`}>
          <Upload className="w-3.5 h-3.5" />
          {busy ? "Uploading…" : dragging ? "Drop plans to upload" : "Drag & drop or upload plan (PDF / image / DWG)"}
          <input type="file" accept={ALLOWED} multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} disabled={busy} />
        </label>
      )}
    </div>
  );
}
